import React, { useState, useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTenantConfig } from '../../store/slices/settingsSlice';
import { setDateRegion } from '../../utils/dateFormat';
import { setNumberRegion } from '../../utils/numberFormat';
import { getLocalized } from '../../i18n/getLocalized';
import { applyFavicon, faviconSource } from '../../utils/documentBranding';
import Sidebar from '../Sidebar';
import Header from '../Header';
import ImpersonationBanner from '../Header/ImpersonationBanner';
import DelegationBanner from '../Header/DelegationBanner';
import ApprovalNotifier from '../ApprovalNotifier';
import './Layout.scss';

function Layout() {
  const dispatch = useDispatch();
  const { config, branding } = useSelector((state) => state.settings);
  const { currentLanguage } = useSelector((state) => state.language);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Tenant default for the sidebar's first presentation (Settings > Defaults).
  // Applied once per mount, when the config arrives — after that the user's
  // own toggling owns the state for the rest of the session.
  const sidebarDefaultApplied = useRef(false);
  const tenantSidebarDefault = config?.defaults?.sidebarState;
  useEffect(() => {
    if (sidebarDefaultApplied.current || !tenantSidebarDefault) return;
    sidebarDefaultApplied.current = true;
    setSidebarCollapsed(tenantSidebarDefault === 'collapsed');
  }, [tenantSidebarDefault]);

  // Supply only the *country* half of the display locale — the tenant config is
  // the sole source for it. The language half is owned by i18n (see i18n/index),
  // which updates the formatters the moment the UI language changes. Passing the
  // language from here too would re-apply a stale value on any unrelated
  // re-render, which is how dates ended up rendering in the previous language.
  // Done during render (not in an effect) so pages below format with the fresh
  // locale in the same render pass.
  setDateRegion(undefined, config?.organization?.country);
  setNumberRegion(undefined, config?.organization?.country);

  useEffect(() => {
    dispatch(fetchTenantConfig());
  }, [dispatch]);

  // The browser tab carries the tenant's own name and logo. The boot script in
  // index.html already applied both from the cached branding, so this is what
  // keeps them right once the fresh payload lands and whenever the UI language
  // changes. The static title in index.html stays the fallback for a tenant
  // that never set a company name.
  //
  // Neither is reverted on unmount: logging out lands on the login page, which
  // is itself tenant-branded, so flipping the tab back to the product name
  // there would contradict the page. Each tenant is its own origin, so nothing
  // leaks between them.
  const companyName = getLocalized(branding?.companyName, currentLanguage);
  const iconUrl = faviconSource(branding);

  useEffect(() => {
    if (companyName) document.title = companyName;
  }, [companyName]);

  useEffect(() => {
    applyFavicon(iconUrl);
  }, [iconUrl]);

  // Apply tenant accent color as global CSS custom properties
  useEffect(() => {
    const accentColor = config?.branding?.accentColor;
    if (accentColor && accentColor !== '#6366f1') {
      document.documentElement.style.setProperty('--accent', accentColor);
      document.documentElement.style.setProperty('--accent-bg', `${accentColor}0F`);
      document.documentElement.style.setProperty('--accent-bg-hover', `${accentColor}1A`);
      document.documentElement.style.setProperty('--accent-light', `${accentColor}CC`);
      document.documentElement.style.setProperty('--accent-focus', `${accentColor}26`);
    } else {
      // Reset to defaults
      document.documentElement.style.removeProperty('--accent');
      document.documentElement.style.removeProperty('--accent-bg');
      document.documentElement.style.removeProperty('--accent-bg-hover');
      document.documentElement.style.removeProperty('--accent-light');
      document.documentElement.style.removeProperty('--accent-focus');
    }
  }, [config?.branding?.accentColor]);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) {
        setMobileOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={`layout ${sidebarCollapsed ? 'layout--collapsed' : ''}`}>
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <div className="layout__main">
        <ImpersonationBanner />
        <DelegationBanner />
        <Header onMobileMenuToggle={() => setMobileOpen(!mobileOpen)} />
        <main className="layout__content">
          <div className="layout__container">
            <Outlet />
          </div>
        </main>
      </div>
      <ApprovalNotifier />
    </div>
  );
}

export default Layout;
